import React from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useAdminScope } from '../contexts/AdminScopeContext';
import {
  DEFAULT_MENU_CATALOG_OWNER,
  DEFAULT_ONLINE_ORDER_CONTROL_MODE,
  DEFAULT_PRODUCT_AVAILABILITY_CONTROL_MODE,
  StoreFormData,
} from '../models/Store';
import { storeService } from '../services/storeService';

const StoreProfileSettings: React.FC = () => {
  const { selectedStore, refreshScopeData } = useAdminScope();
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<StoreFormData | null>(null);
  const savedFormRef = React.useRef<StoreFormData | null>(null);
  const preserveFeedbackOnNextStoreRefreshRef = React.useRef(false);

  React.useEffect(() => {
    if (!selectedStore) {
      setForm(null);
      savedFormRef.current = null;
      return;
    }

    const nextForm: StoreFormData = {
      name: selectedStore.name,
      address: selectedStore.address,
      location: {
        latitude: selectedStore.location?.latitude ?? 0,
        longitude: selectedStore.location?.longitude ?? 0,
      },
      isActive: selectedStore.isActive,
      isDeliveryResponsible: selectedStore.isDeliveryResponsible ?? false,
      operationGroupId: selectedStore.operationGroupId ?? null,
      isVisibleOnline: selectedStore.isVisibleOnline ?? false,
      isPickupEnabled: selectedStore.isPickupEnabled ?? true,
      isDeliveryEnabled: selectedStore.isDeliveryEnabled ?? true,
      onlineOrderControlMode:
        selectedStore.onlineOrderControlMode || DEFAULT_ONLINE_ORDER_CONTROL_MODE,
      productAvailabilityControlMode:
        selectedStore.productAvailabilityControlMode ||
        DEFAULT_PRODUCT_AVAILABILITY_CONTROL_MODE,
      menuCatalogOwner: selectedStore.menuCatalogOwner || DEFAULT_MENU_CATALOG_OWNER,
      phone: selectedStore.phone || '',
      email: selectedStore.email || '',
    };

    setForm(nextForm);
    savedFormRef.current = nextForm;
    if (preserveFeedbackOnNextStoreRefreshRef.current) {
      preserveFeedbackOnNextStoreRefreshRef.current = false;
    } else {
      setError(null);
      setSuccess(null);
    }
  }, [selectedStore]);

  const patch = <K extends keyof StoreFormData>(key: K, value: StoreFormData[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setSuccess(null);
  };

  const patchLocation = (key: 'latitude' | 'longitude', rawValue: string) => {
    const parsed = Number(rawValue);
    setForm((prev) =>
      prev
        ? {
            ...prev,
            location: {
              ...prev.location,
              [key]: Number.isFinite(parsed) ? parsed : 0,
            },
          }
        : prev
    );
    setSuccess(null);
  };

  const isSameProfileExceptControlModes = (
    current: StoreFormData,
    saved: StoreFormData
  ): boolean =>
    current.name === saved.name &&
    current.address === saved.address &&
    current.location.latitude === saved.location.latitude &&
    current.location.longitude === saved.location.longitude &&
    current.isActive === saved.isActive &&
    (current.isDeliveryResponsible ?? false) ===
      (saved.isDeliveryResponsible ?? false) &&
    (current.operationGroupId ?? null) === (saved.operationGroupId ?? null) &&
    (current.isVisibleOnline ?? false) === (saved.isVisibleOnline ?? false) &&
    (current.isPickupEnabled ?? true) === (saved.isPickupEnabled ?? true) &&
    (current.isDeliveryEnabled ?? true) === (saved.isDeliveryEnabled ?? true) &&
    (current.phone || '') === (saved.phone || '') &&
    (current.email || '') === (saved.email || '');

  const handleSave = async () => {
    if (!selectedStore || !form) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const savedForm = savedFormRef.current;
      const nextControlMode =
        form.onlineOrderControlMode || DEFAULT_ONLINE_ORDER_CONTROL_MODE;
      const savedControlMode =
        savedForm?.onlineOrderControlMode || DEFAULT_ONLINE_ORDER_CONTROL_MODE;
      const nextAvailabilityControlMode =
        form.productAvailabilityControlMode ||
        DEFAULT_PRODUCT_AVAILABILITY_CONTROL_MODE;
      const savedAvailabilityControlMode =
        savedForm?.productAvailabilityControlMode ||
        DEFAULT_PRODUCT_AVAILABILITY_CONTROL_MODE;
      const nextMenuCatalogOwner =
        form.menuCatalogOwner || DEFAULT_MENU_CATALOG_OWNER;
      const savedMenuCatalogOwner =
        savedForm?.menuCatalogOwner || DEFAULT_MENU_CATALOG_OWNER;
      const isControlModesOnlySave =
        !!savedForm &&
        (nextControlMode !== savedControlMode ||
          nextAvailabilityControlMode !== savedAvailabilityControlMode ||
          nextMenuCatalogOwner !== savedMenuCatalogOwner) &&
        isSameProfileExceptControlModes(form, savedForm);

      if (isControlModesOnlySave) {
        await storeService.updateStoreControlModes(
          selectedStore.id,
          nextControlMode,
          nextAvailabilityControlMode,
          nextMenuCatalogOwner
        );
        preserveFeedbackOnNextStoreRefreshRef.current = true;
        await refreshScopeData();
        savedFormRef.current = {
          ...form,
          onlineOrderControlMode: nextControlMode,
          productAvailabilityControlMode: nextAvailabilityControlMode,
          menuCatalogOwner: nextMenuCatalogOwner,
        };
        setForm((prev) =>
          prev
            ? {
                ...prev,
                onlineOrderControlMode: nextControlMode,
                productAvailabilityControlMode: nextAvailabilityControlMode,
                menuCatalogOwner: nextMenuCatalogOwner,
              }
            : prev
        );
        setSuccess('Modes de contrôle enregistrés.');
        return;
      }

      preserveFeedbackOnNextStoreRefreshRef.current = true;
      await storeService.updateStore(selectedStore.id, form);
      await refreshScopeData();
      savedFormRef.current = {
        ...form,
        onlineOrderControlMode:
          form.onlineOrderControlMode || DEFAULT_ONLINE_ORDER_CONTROL_MODE,
        productAvailabilityControlMode:
          form.productAvailabilityControlMode ||
          DEFAULT_PRODUCT_AVAILABILITY_CONTROL_MODE,
        menuCatalogOwner: form.menuCatalogOwner || DEFAULT_MENU_CATALOG_OWNER,
      };
      setSuccess('Profil magasin enregistré.');
    } catch (e: any) {
      preserveFeedbackOnNextStoreRefreshRef.current = false;
      setError(e?.message || 'Erreur lors de l’enregistrement du profil magasin.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Profil magasin
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Édition du magasin actif. Le rattachement au groupe se gère depuis la page Magasins.
          </Typography>
        </Box>

        {!selectedStore || !form ? (
          <Alert severity="warning">Aucun magasin actif sélectionné.</Alert>
        ) : (
          <>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}

            <Alert severity="info">
              Groupe opérationnel: {selectedStore.operationGroupId || 'Non défini'}
            </Alert>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(12, minmax(0, 1fr))' },
                gap: 2,
              }}
            >
              <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 6' } }}>
                <TextField
                  fullWidth
                  label="Nom du magasin"
                  value={form.name}
                  onChange={(e) => patch('name', e.target.value)}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 6' } }}>
                <TextField
                  fullWidth
                  label="Téléphone"
                  value={form.phone || ''}
                  onChange={(e) => patch('phone', e.target.value)}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 12' } }}>
                <TextField
                  fullWidth
                  label="Adresse"
                  value={form.address}
                  onChange={(e) => patch('address', e.target.value)}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 6' } }}>
                <TextField
                  fullWidth
                  label="Email"
                  value={form.email || ''}
                  onChange={(e) => patch('email', e.target.value)}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 3' } }}>
                <TextField
                  fullWidth
                  label="Latitude"
                  type="number"
                  value={form.location.latitude}
                  onChange={(e) => patchLocation('latitude', e.target.value)}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 3' } }}>
                <TextField
                  fullWidth
                  label="Longitude"
                  type="number"
                  value={form.location.longitude}
                  onChange={(e) => patchLocation('longitude', e.target.value)}
                />
              </Box>
            </Box>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(_, checked) => patch('isActive', checked)}
                  />
                }
                label="Magasin actif"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isVisibleOnline ?? false}
                    onChange={(_, checked) => patch('isVisibleOnline', checked)}
                  />
                }
                label="Visible online"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isPickupEnabled ?? true}
                    onChange={(_, checked) => patch('isPickupEnabled', checked)}
                  />
                }
                label="Pickup activé"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isDeliveryEnabled ?? true}
                    onChange={(_, checked) => patch('isDeliveryEnabled', checked)}
                  />
                }
                label="Livraison activée"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isDeliveryResponsible ?? false}
                    onChange={(_, checked) => patch('isDeliveryResponsible', checked)}
                  />
                }
                label="Responsable livraison du groupe"
              />
            </Stack>

            <Box>
              <TextField
                select
                fullWidth
                label="Contrôle commandes online"
                value={form.onlineOrderControlMode || DEFAULT_ONLINE_ORDER_CONTROL_MODE}
                onChange={(e) =>
                  patch(
                    'onlineOrderControlMode',
                    e.target.value as StoreFormData['onlineOrderControlMode']
                  )
                }
                helperText="FIRST_PREPARE_WINS: Step Store ou POSCaisse prend la main au premier Préparer. POSCAISSE_ONLY: Step Store reste en lecture seule sauf commandes déjà prises par Step Store."
              >
                <MenuItem value="FIRST_PREPARE_WINS">
                  Premier Préparer prend le flux
                </MenuItem>
                <MenuItem value="POSCAISSE_ONLY">POSCaisse maître</MenuItem>
              </TextField>
            </Box>

            <Box>
              <TextField
                select
                fullWidth
                label="Contrôle disponibilité produits"
                value={
                  form.productAvailabilityControlMode ||
                  DEFAULT_PRODUCT_AVAILABILITY_CONTROL_MODE
                }
                onChange={(e) =>
                  patch(
                    'productAvailabilityControlMode',
                    e.target.value as StoreFormData['productAvailabilityControlMode']
                  )
                }
                helperText="STEP_STORE: Step Store/admin-app peuvent gérer la disponibilité online. POSCAISSE: Step Store devient lecture seule avec urgence contrôlée par code admin."
              >
                <MenuItem value="STEP_STORE">Step Store / admin-app</MenuItem>
                <MenuItem value="POSCAISSE">POSCaisse maître</MenuItem>
              </TextField>
            </Box>

            <Box>
              <TextField
                select
                fullWidth
                label="Source du catalogue menu"
                value={form.menuCatalogOwner || DEFAULT_MENU_CATALOG_OWNER}
                onChange={(e) =>
                  patch(
                    'menuCatalogOwner',
                    e.target.value as StoreFormData['menuCatalogOwner']
                  )
                }
                helperText="Admin-app / StepBurger: menu éditable ici. POSCaisse / BackOffice: admin-app passe en lecture seule pour éviter les conflits."
              >
                <MenuItem value="ADMIN_APP">Admin-app / StepBurger</MenuItem>
                <MenuItem value="POSCAISSE">POSCaisse / BackOffice</MenuItem>
              </TextField>
            </Box>

            <Box>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default StoreProfileSettings;
